import {
  useAcceptCompanyUserInviteMutation,
  useCompanyUserInviteByTokenQuery,
  useCreateUserMutation,
  useLoginByCredentialsMutation,
} from "@generated/graphql";
import { LockOutlined, MailOutline } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Card, Stack, Typography } from "@mui/material";
import LogoImg from "assets/images/logo.svg";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import { COMPANY_USER_ROLE_TITLE } from "constants/company";
import ROUTES from "constants/routes";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { authRedirect, setAuthCookies } from "helpers/cookieHelper";
import { getApolloErrorMessages, parseApolloError } from "helpers/errorsParser";
import VALIDATION from "helpers/validation";
import { cleanSpaceInString } from "helpers/validation/createValidation";
import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

type InviteFormInputs = {
  email: string;
  firstName: string;
  lastName: string;
  middleName: string;
  password: string;
  confirmPassword: string;
};

type InviteMode = "register" | "login";

const DEFAULT_VALUES: InviteFormInputs = {
  email: "",
  firstName: "",
  lastName: "",
  middleName: "",
  password: "",
  confirmPassword: "",
};

const DUPLICATE_EMAIL_ERROR_TEXT = "Email должен быть уникальным";

const FIELD_LABEL_PROPS = {
  variant: "body16rg" as const,
  sx: {
    color: "base.500",
    fontWeight: 600,
    mb: 0.5,
  },
};

const renderPageCard = (content: ReactNode) => (
  <Card
    sx={{
      width: "100%",
      maxWidth: 580,
      px: { xs: 3, sm: 5.5 },
      py: { xs: 3.5, sm: 5 },
      borderRadius: 4,
      backgroundColor: "white",
      boxShadow: "0 22px 64px rgba(23, 73, 209, 0.16)",
    }}>
    <Stack spacing={{ xs: 3, sm: 4 }}>
      <Box
        component='img'
        src={LogoImg}
        alt='MyHRBox'
        sx={{ width: 172, maxWidth: "100%", height: "auto" }}
      />
      {content}
    </Stack>
  </Card>
);

const CompanyInvitePage = () => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<InviteMode>("register");
  const token = searchParams.get("token") || "";

  const form = useForm<InviteFormInputs>({
    defaultValues: DEFAULT_VALUES,
  });
  const { handleSubmit, reset, setError } = form;

  const {
    data: inviteData,
    loading: isInviteLoading,
    error: inviteError,
  } = useCompanyUserInviteByTokenQuery({
    variables: { token },
    skip: !token,
    fetchPolicy: "network-only",
  });
  const [createUser, { loading: isCreateUserLoading }] = useCreateUserMutation();
  const [loginByCredentials, { loading: isLoginLoading }] = useLoginByCredentialsMutation();
  const [acceptCompanyUserInvite, { loading: isAcceptInviteLoading }] = useAcceptCompanyUserInviteMutation();

  const invite = inviteData?.companyUserInviteByToken;
  const companyName = invite?.companyFullName || invite?.companyShortName || "Компания";
  const isRegisterMode = mode === "register";
  const isLoading = isCreateUserLoading || isLoginLoading || isAcceptInviteLoading;

  useEffect(() => {
    if (!invite?.email) return;

    reset({
      ...DEFAULT_VALUES,
      email: invite.email,
    });
  }, [invite?.email, reset]);

  const roleTitle = useMemo(() => {
    if (!invite) return "";

    return COMPANY_USER_ROLE_TITLE[invite.role] || invite.position || "";
  }, [invite]);

  const handleRedirectAfterAuth = (accessToken: string, refreshToken: string) => {
    authRedirect({
      accessToken,
      refreshToken,
      redirectUrl: `/${ROUTES.LOGIN}`,
    });
  };

  const handleInviteAcceptedLogin = async (password: string) => {
    if (!invite || !token) return;

    try {
      const result = await loginByCredentials({
        variables: {
          email: invite.email,
          password,
        },
      });
      const accessToken = result.data?.loginByCredentials.accessToken;
      const refreshToken = result.data?.loginByCredentials.refreshToken;

      if (!accessToken || !refreshToken) {
        addToast({ type: ToastTypeEnum.ERROR, text: "Произошла ошибка при авторизации" });
        return;
      }

      setAuthCookies([
        { name: "accessToken", value: accessToken },
        { name: "refreshToken", value: refreshToken },
      ]);

      try {
        await acceptCompanyUserInvite({
          variables: {
            input: { token },
          },
        });
      } catch (error) {
        parseApolloError(
          error,
          {
            addToast,
            defaultText: "Не удалось завершить принятие приглашения",
          },
          {
            errorTypes: [{ name: "graphQLErrors" }, { name: "networkError" }],
          }
        );
      }

      handleRedirectAfterAuth(accessToken, refreshToken);
    } catch {
      setError("password", { message: "Неверный пароль" });
    }
  };

  const onSubmit = async (formData: InviteFormInputs) => {
    if (!invite || !token) return;

    if (!isRegisterMode) {
      await handleInviteAcceptedLogin(formData.password);
      return;
    }

    try {
      const result = await createUser({
        variables: {
          input: {
            email: invite.email,
            firstName: cleanSpaceInString(formData.firstName),
            lastName: cleanSpaceInString(formData.lastName),
            middleName: cleanSpaceInString(formData.middleName),
            password: formData.password,
            inviteToken: token,
          },
        },
      });
      const accessToken = result.data?.createUser.accessToken;
      const refreshToken = result.data?.createUser.refreshToken;

      if (!accessToken || !refreshToken) {
        addToast({ type: ToastTypeEnum.ERROR, text: "Произошла ошибка при создании аккаунта" });
        return;
      }

      handleRedirectAfterAuth(accessToken, refreshToken);
    } catch (error) {
      const errorMessages = getApolloErrorMessages(error, {
        errorTypes: [{ name: "graphQLErrors" }, { name: "networkError" }],
      });
      const isDuplicateEmailError = errorMessages?.some((message) => message.includes(DUPLICATE_EMAIL_ERROR_TEXT));

      if (isDuplicateEmailError) {
        setMode("login");
        reset({
          ...DEFAULT_VALUES,
          email: invite.email,
        });
        addToast({
          type: ToastTypeEnum.WARNING,
          text: "Аккаунт для этой почты уже существует. Введите пароль, чтобы войти.",
        });
        return;
      }

      parseApolloError(error, {
        addToast,
        defaultText: "Не удалось обработать приглашение",
      });
    }
  };

  const handleGoToLogin = () => navigate(`/${ROUTES.LOGIN}`);

  if (!token) {
    return renderPageCard(
      <Stack spacing={2.5}>
        <Typography variant='h28'>Приглашение недоступно</Typography>
        <Typography color='text.secondary'>
          В ссылке отсутствует токен приглашения. Проверьте письмо и попробуйте перейти по ссылке еще раз.
        </Typography>
        <Button
          variant='contained'
          size='large'
          onClick={handleGoToLogin}>
          На страницу входа
        </Button>
      </Stack>
    );
  }

  if (isInviteLoading) {
    return renderPageCard(
      <Stack spacing={1.5}>
        <Typography variant='h28'>Загрузка приглашения</Typography>
        <Typography color='text.secondary'>Получаем данные компании и параметры доступа.</Typography>
      </Stack>
    );
  }

  if (inviteError || !invite) {
    return renderPageCard(
      <Stack spacing={2.5}>
        <Typography variant='h28'>Приглашение недоступно</Typography>
        <Typography color='text.secondary'>
          Не удалось получить данные приглашения. Возможно, ссылка устарела или была отозвана.
        </Typography>
        <Button
          variant='contained'
          size='large'
          onClick={handleGoToLogin}>
          На страницу входа
        </Button>
      </Stack>
    );
  }

  return renderPageCard(
    <Form
      form={form}
      onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={{ xs: 3, sm: 3.5 }}>
        <Stack spacing={2}>
          <Typography
            variant='h24'
            sx={{
              color: "base.700",
              lineHeight: 1.25,
              fontWeight: 500,
            }}>
            {isRegisterMode ? "Добрый день" : "С возвращением"}
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "third.main",
            }}>
            <Typography
              variant='body16rg'
              sx={{
                color: "blue.300",
                lineHeight: 1.55,
              }}>
              <Box
                component='span'
                sx={{ color: "base.700", fontWeight: 700 }}>
                {companyName}
              </Box>
              {" приглашает вас в рабочее пространство"}
              {roleTitle ? " в роли " : ""}
              {roleTitle && (
                <Box
                  component='span'
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1.25,
                    py: 0.25,
                    borderRadius: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "white",
                    color: "primary.main",
                    fontWeight: 500,
                    verticalAlign: "middle",
                  }}>
                  {roleTitle}
                </Box>
              )}
            </Typography>
          </Box>
        </Stack>

        <TextFieldControl
          name='email'
          type='email'
          variant='outlined'
          label='Почта для входа'
          labelTypographyProps={FIELD_LABEL_PROPS}
          InputProps={{ readOnly: true }}
          startIcon={<MailOutline sx={{ color: "base.300" }} />}
          endIcon={<LockOutlined sx={{ color: "base.300" }} />}
        />

        {isRegisterMode && (
          <>
            <TextFieldControl
              name='lastName'
              label='Фамилия'
              labelTypographyProps={FIELD_LABEL_PROPS}
              type='text'
              variant='outlined'
              rules={{ required: true, ...VALIDATION.userName }}
            />

            <TextFieldControl
              name='firstName'
              label='Имя'
              labelTypographyProps={FIELD_LABEL_PROPS}
              type='text'
              variant='outlined'
              rules={{ required: true, ...VALIDATION.userName }}
            />

            <TextFieldControl
              name='middleName'
              label='Отчество'
              labelTypographyProps={FIELD_LABEL_PROPS}
              type='text'
              variant='outlined'
              rules={{ required: true, ...VALIDATION.userName }}
            />
          </>
        )}

        <TextFieldControl
          name='password'
          label={isRegisterMode ? "Создайте пароль" : "Введите пароль"}
          labelTypographyProps={FIELD_LABEL_PROPS}
          type='password'
          variant='outlined'
          placeholder={isRegisterMode ? "Минимум 8 символов" : "Введите пароль"}
          rules={{
            required: true,
            minLength: isRegisterMode
              ? {
                  value: 8,
                  message: "Минимум 8 символов",
                }
              : undefined,
          }}
        />

        {isRegisterMode && (
          <TextFieldControl
            name='confirmPassword'
            label='Повторите пароль'
            labelTypographyProps={FIELD_LABEL_PROPS}
            type='password'
            variant='outlined'
            placeholder='Повторите пароль'
            rules={{
              required: true,
              validate: (value, formValues) => value === formValues.password || "Пароли не совпадают",
            }}
          />
        )}

        <LoadingButton
          type='submit'
          loading={isLoading}
          variant='contained'
          size='large'
          sx={{ mt: 0.5 }}>
          {isRegisterMode ? "Создать аккаунт и войти" : "Войти и принять приглашение"}
        </LoadingButton>
      </Stack>
    </Form>
  );
};

export default CompanyInvitePage;
